import { PASSWORD_RESET_VALIDITY_HOURS } from '@daymade/common-lib/constants';
import { invalidPasswordMessage } from '@daymade/common-lib/validationRules';
import Form from 'components/common/Form/Form';
import Message from 'components/common/Message/Message';
import MainLayout from 'components/layout/MainLayout';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import PageTitle from 'components/PageTitle';
import RoundedButton from 'components/RoundedButton';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { ReactNode, useEffect, useState } from 'react';
import css from 'styled-jsx/css';
import {
  passwordResetErrorMessageFallback,
  passwordResetFriendlyErrorMessages,
} from 'utils/api/authApi';
import { tryApi } from 'utils/api/common';
import { authApiClient } from 'utils/apiClient';
import { setCookie } from 'utils/cookie';
import HttpStatusCode from 'utils/HttpStatusCode';
import { validPassword } from 'utils/RegistrationValidator';

const PasswordReset = observer(() => {
  const { currentUser, updateCurrentUser } = useUserStore();
  const { displayToast, navigateToDashboard } = useUIStore();
  const router = useRouter();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ReactNode>('');
  const [tokenRequestSuccessful, setTokenRequestSuccessful] = useState(false);
  const passwordResetToken = Array.isArray(router.query.token)
    ? router.query.token[0]
    : router.query.token;

  useEffect(() => {
    if (currentUser) {
      router.push('/dashboard');
    }
  }, [currentUser]);

  const handleRequestLinkButtonPress = async () => {
    setLoading(true);
    const response = await tryApi(
      authApiClient.public.requestPasswordReset({
        body: { emailAddress: email },
      }),
    );
    setLoading(false);
    if (response.status == HttpStatusCode.OK) {
      setTokenRequestSuccessful(true);
      setEmail('');
    } else if (response.status == HttpStatusCode.BAD_REQUEST) {
      setError(
        passwordResetFriendlyErrorMessages[response.body.reason] ||
          passwordResetErrorMessageFallback,
      );
    } else {
      setError('An unknown error occurred');
    }
  };

  const renderRequestTokenForm = () => {
    if (!tokenRequestSuccessful) {
      return (
        // We must provide keys for the forms, otherwise components from each form are reused when switching from
        // changePasswordForm to requestTokenForm
        <Form
          key="requestTokenForm"
          id="form"
          onSubmit={handleRequestLinkButtonPress}
        >
          <Form.Field>
            <label>Email Address</label>
            <input
              id="emailField"
              placeholder="Email Address"
              type="text"
              autoComplete="username"
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              className="neonInput"
            />
          </Form.Field>
          <input
            type="submit"
            style={{ visibility: 'hidden', position: 'absolute' }}
          />
          <RoundedButton
            id="requestLinkButton"
            onClick={handleRequestLinkButtonPress}
            loading={loading}
            disabled={!email}
          >
            Request Email Link
          </RoundedButton>
          {error && (
            <Message
              id="genericMessage"
              negative
              header="Whoops..."
              content={error}
            />
          )}
        </Form>
      );
    } else {
      return (
        <Message
          id="genericMessage"
          info
          header="Password reset link sent"
          content={
            'An email with a password reset link has been sent to you. The link will expire in ' +
            PASSWORD_RESET_VALIDITY_HOURS +
            'h.'
          }
        />
      );
    }
  };

  const handleChangePasswordButtonPress = async () => {
    if (!validPassword(password)) {
      setError(invalidPasswordMessage);
      return;
    }

    if (!passwordResetToken) {
      setError('Sorry, the password reset token is missing.');
      return;
    }

    setLoading(true);

    const res = await tryApi(
      authApiClient.public.usePasswordResetToken({
        body: {
          resetToken: passwordResetToken,
          newPassword: password,
        },
      }),
    );

    if (res.status == HttpStatusCode.OK && res.body.success) {
      setCookie('jwt', res.body.jwtToken, { secure: true, expires: 30 });
      localStorage.setItem('previously_logged_in', 'true');

      displayToast({
        title: 'Successfully changed password.',
        timeout: 3500,
      });

      await updateCurrentUser();
      navigateToDashboard();
    } else if (res.status == HttpStatusCode.BAD_REQUEST) {
      setLoading(false);
      setError(
        passwordResetFriendlyErrorMessages[res.body.reason] ||
          passwordResetErrorMessageFallback,
      );
    } else {
      setLoading(false);
      setError(passwordResetErrorMessageFallback);
    }
  };

  const renderChangePasswordForm = () => (
    // We must provide keys for the forms, otherwise components from each form are reused when switching from
    // changePasswordForm to requestTokenForm
    <Form
      key="changePasswordForm"
      id="form"
      onSubmit={handleChangePasswordButtonPress}
    >
      <Form.Field>
        <label>New Password</label>
        <input
          placeholder="New Password"
          type="password"
          autoComplete="new-password"
          onChange={(e) => {
            setPassword(e.target.value);
            setError('');
          }}
          className="neonInput"
        />
      </Form.Field>
      <input
        type="submit"
        style={{ visibility: 'hidden', position: 'absolute' }}
      />
      <RoundedButton
        id="changePasswordButton"
        onClick={handleChangePasswordButtonPress}
        loading={loading}
        disabled={!password}
      >
        Change Password
      </RoundedButton>
      {error && (
        <Message
          id="genericMessage"
          negative
          header="Whoops..."
          content={error}
        />
      )}
    </Form>
  );

  return (
    <MainLayout>
      <NextSeo
        title="Password Reset | Win Epic Prizes | DAYMADE"
        openGraph={{
          title: 'Password Reset | Win Epic Prizes | DAYMADE',
        }}
      />

      <PageTitle>
        Password <strong>Reset</strong>
      </PageTitle>

      <PaddedMaxWidthContainer
        style={{ paddingTop: '2em', paddingBottom: '2em' }}
      >
        {passwordResetToken
          ? renderChangePasswordForm()
          : renderRequestTokenForm()}
      </PaddedMaxWidthContainer>

      <style jsx>{styles}</style>
    </MainLayout>
  );
});

const styles = css`
  :global(#form, #genericMessage) {
    display: flex;
    flex-direction: column;
  }

  :global(#form input[type='text'], #form input[type='password']) {
    font-size: 16px;
  }
`;

export default PasswordReset;
